import {
  ActionButton,
  OfficeButton,
  OfficeHeader,
  Paginator,
  RegularInsurerPicker,
  TableComponent,
} from "components";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";

import { Action } from "components/buttons/action-button";
import CreateTreatyClaimContainer from "./create";
import { FC } from "react";
import RemoveContainer from "./remove";
import UpdateTreatyClaimContainer from "./update";
import ViewTreatyClaimContainer from "./view";
import moment from "moment";
import numeral from "numeral";
import { usePagination } from "hooks";
import { useTreatyClaims } from "hooks/data/use-treaty-claims";
import { withPermissions } from "utils";

type Props = {};

const TreatyClaimsPage: FC<Props> = () => {
  const navigate = useAppNavigate();
  const searchParams = useAppSearch();
  const { offset, limit, setPage } = usePagination();
  const { claims, count, loading, refetch } = useTreatyClaims({
    treatyId: "",
    insurerId: (searchParams?.insurer as string) || "",
    limit: limit,
    offset: offset,
  });

  const navigateItem = (id: string, claim_number: string) => () => {
    navigate({
      to: `./${id}/participants/${claim_number?.replaceAll("/", "-")}`,
      search: (old) => ({
        ...old,
        policy: claim_number,
        // layers: layers,
      }),
    });
  };

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader
        renderActions={() => (
          <>
            {withPermissions(["*:*", "claims:manage", "claims:create"])(
              <OfficeButton
                onClick={() =>
                  navigate({
                    search(prev) {
                      return {
                        ...prev,
                        modal: "create",
                      };
                    },
                  })
                }
              >
                Create Claim
              </OfficeButton>
            )}
          </>
        )}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            loading={loading}
            title={"treaty claims"}
            hasSearch
            columns={[
              {
                name: "Treaty Ref#",
                accessor: "treaty.treaty_reference",
              },
              {
                name: "Reinsured",
                accessor: "treaty.insurer.insurer_company_name",
              },
              {
                name: "Treaty Program",
                accessor: "treaty.treaty_program.treaty_name",
              },
              {
                name: "Claim #",
                accessor: "treaty_reference",
                render: (value) => (
                  <div className="flex flex-col leading-0">
                    <span className="font-medium text-primary">
                      {value?.claim_number}
                    </span>
                    {/* {value?.layer_number} */}
                  </div>
                ),
              },
              {
                name: "Layer #",
                accessor: "layer_number",
              },
              {
                name: "Insured | Date of Loss",
                accessor: "employee.employee_first_name",
                render: (value) => (
                  <div className="flex flex-col leading-0">
                    <span className="font-medium text-primary">
                      {value?.insured_name || "N/A"}
                    </span>
                    <span className="font-light text-primary">
                      {moment(value?.date_of_loss).format("DD/MM/YYYY")}
                    </span>
                  </div>
                ),
              },
              {
                name: "Claim Amount",
                accessor: "claim_paid",
                render: (value) => (
                  <div className="flex flex-col leading-0">
                    <span className="font-medium text-primary">
                      {value?.treaty?.currency}{" "}
                      {numeral(value?.claim_paid).format("0,0.00") || "N/A"}
                    </span>
                  </div>
                ),
              },
              {
                name: "Actions",
                accessor: "actions",
                render: (value) => (
                  <>
                    <ActionButton
                      action={"view"}
                      tooltip="View Claim"
                      onClick={dispatchAction(
                        value?.treaty_claim_id || "",
                        "view"
                      )}
                    />
                    {withPermissions([
                      "*:*",
                      "treaties:*",
                      "treaties:manage",
                      "treaties:update",
                    ])(
                      <ActionButton
                        action={"update"}
                        tooltip="Update Claim"
                        onClick={dispatchAction(
                          value?.treaty_claim_id || "",
                          "update"
                        )}
                      />
                    )}
                    {withPermissions([
                      "*:*",
                      "treaties:*",
                      "treaties:manage",
                      "treaties:delete",
                    ])(
                      <ActionButton
                        action={"remove"}
                        tooltip="Remove Claim"
                        onClick={dispatchAction(
                          value?.treaty_claim_id || "",
                          "remove"
                        )}
                      />
                    )}
                    <ActionButton
                      action={"expand"}
                      tooltip="View Claim Participants & Payments"
                      onClick={navigateItem(
                        value?.treaty_claim_id || "",
                        value?.claim_number || ""
                      )}
                    />
                  </>
                ),
              },
            ]}
            data={claims}
            refetch={refetch}
            renderActions={() => (
              <>
                <RegularInsurerPicker
                  id={"insurer"}
                  label={""}
                  rawId
                  values={{
                    insurer: searchParams?.insurer || "",
                  }}
                  setFieldValue={(e: any, value: any) =>
                    navigate({
                      search(prev) {
                        return {
                          ...prev,
                          insurer: value,
                        };
                      },
                    })
                  }
                  setFieldTouched={() => {}}
                  setFieldError={() => {}}
                />
              </>
            )}
            // renderItem={(item) => <SingleClaim item={item} dispatchAction={dispatchAction} />}

            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={count}
                currentSize={claims.length}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>
      <CreateTreatyClaimContainer
        open={searchParams?.modal === "create"}
        setOpen={(val) =>
          navigate({
            search(prev) {
              return {
                ...prev,
                modal: val ? "create" : undefined,
              };
            },
          })
        }
      />
      {searchParams?.id && searchParams?.modal === "view" && (
        <>
          <ViewTreatyClaimContainer
            open={searchParams?.modal === "view"}
            setOpen={(val) =>
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    modal: val ? "view" : undefined,
                  };
                },
              })
            }
          />
        </>
      )}
      {searchParams?.id && searchParams?.modal === "update" && (
        <UpdateTreatyClaimContainer
          open={true}
          setOpen={(val) =>
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "update" : undefined,
                };
              },
            })
          }
        />
      )}
      {searchParams?.id && searchParams?.modal === "remove" && (
        <RemoveContainer
          open={true}
          setOpen={(val) =>
            navigate({
              search(prev) {
                return {
                  ...prev,
                  modal: val ? "remove" : undefined,
                };
              },
            })
          }
        />
      )}
    </main>
  );
};

export default TreatyClaimsPage;
